import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { ReactNode, useMemo } from 'react';
import {
  Cell,
  HeaderCell,
  TableHeader,
  TableContainer,
  TableRow,
  TableBody,
} from './styles';
import { ColumnType } from './types';

export interface TableProps<T> {
  columns: ColumnType<T>[];
  data: T[];
  onRowClick?: (record: T) => void;
  onDragEnd?: (oldIndex: number, newIndex: number) => void;
  loading?: boolean;
  emptyText?: string | ReactNode;
  clickableColumn?: number;
  disableDrag?: (record: T) => boolean;
}

const DraggableTableRow = <T extends { id: string | number }>({
  record,
  children,
  onClick,
  disableDrag,
  columnWidths,
}: {
  record: T;
  children: ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  disableDrag?: boolean;
  isLast?: boolean;
  columnWidths: string[];
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: String(record.id),
    disabled: disableDrag,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: onClick ? 'pointer' : 'default',
  } as const;

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      $columnWidths={columnWidths}
      {...attributes}
    >
      {React.Children.map(children, (child, index) => {
        if (index === 0) {
          return React.cloneElement(child as React.ReactElement, {
            ...listeners,
          });
        }

        return child;
      })}
    </TableRow>
  );
};

export function DraggableTable<T extends { id: string | number }>({
  columns,
  data,
  onRowClick,
  clickableColumn = 1,
  onDragEnd,
  loading,
  emptyText = 'No data',
  disableDrag,
}: TableProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const columnWidths = useMemo(
    () => columns.map((column) => column.width ?? '1fr'),
    [columns],
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!data.length) {
    return <div>{emptyText}</div>;
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = data.findIndex(
        (item) => String(item.id) === String(active.id),
      );
      const newIndex = data.findIndex(
        (item) => String(item.id) === String(over.id),
      );

      // if (newIndex === data.length - 1) {
      //   return;
      // }
      onDragEnd?.(oldIndex, newIndex);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      cancelDrop={() => false}
    >
      <TableContainer>
        <TableHeader $columnWidths={columnWidths}>
          {columns.map((column) => (
            <HeaderCell key={column.key} style={{ width: column.width }}>
              {column.title}
            </HeaderCell>
          ))}
        </TableHeader>

        <SortableContext
          items={data.map((item) => String(item.id))}
          strategy={verticalListSortingStrategy}
        >
          <TableBody>
            {data.map((record) => (
              <DraggableTableRow
                key={record.id}
                record={record}
                disableDrag={disableDrag && disableDrag(record)}
                isLast={data.indexOf(record) === data.length - 1}
                columnWidths={columnWidths}
              >
                {columns.map((column, index) => (
                  <Cell
                    key={`${record.id}-${column.key}`}
                    onClick={(e) => {
                      if (
                        column.key === 'templateName' ||
                        column.key === 'taskName'
                      ) {
                        e.preventDefault();
                        onRowClick?.(record);
                      }
                    }}
                    style={{
                      cursor: index === clickableColumn ? 'pointer' : 'default',
                    }}
                  >
                    {column.render
                      ? column.render(
                          record[column.key as keyof T],
                          record,
                          data.indexOf(record),
                          data,
                        )
                      : String(record[column.key as keyof T])}
                  </Cell>
                ))}
              </DraggableTableRow>
            ))}
          </TableBody>
        </SortableContext>
      </TableContainer>
    </DndContext>
  );
}
